import React, { useRef, useState, useEffect } from "react";
import {
  Plus, Mic, ArrowUp, Paperclip, Camera, Image as ImageIcon, Database,
  Cloud, Github, X, FileText, Loader2, Square,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function Composer({ onSend, onDataset, config, disabled, activeModule }) {
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [doc, setDoc] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  const taRef = useRef(null);
  const fileRef = useRef(null);
  const imgRef = useRef(null);
  const dataRef = useRef(null);
  const recogRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    const ta = taRef.current;
    if (ta) { ta.style.height = "auto"; ta.style.height = Math.min(ta.scrollHeight, 200) + "px"; }
  }, [text]);

  useEffect(() => {
    if (!menuOpen) return;
    const onEsc = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [menuOpen]);

  const doUpload = async (file) => {
    try {
      setUploading(true);
      const res = await api.upload(file);
      if (res.kind === "data") {
        onDataset(res);
        toast.success(`Dataset loaded: ${res.filename} (${res.analysis?.rowCount} rows)`);
      } else if (res.kind === "image") {
        setImage({ base64: res.base64, name: res.filename });
      } else if (res.kind === "document") {
        setDoc({ text: res.text, name: res.filename, charCount: res.charCount, note: res.note });
        if (res.note) toast.message(res.note);
        else toast.success(`Document ready: ${res.filename}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setMenuOpen(false);
    }
  };

  const handleFiles = (list) => { Array.from(list || []).forEach(doUpload); };

  const send = () => {
    if (disabled || uploading) return;
    if (!text.trim() && !image && !doc) return;
    onSend({
      message: text.trim(),
      imageBase64: image?.base64 || null,
      imageName: image?.name,
      documentText: doc?.text || null,
      documentName: doc?.name,
      module: activeModule,
    });
    setText(""); setImage(null); setDoc(null);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // Voice (Web Speech API)
  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input is not supported in this browser."); return; }
    if (recording) { recogRef.current?.stop(); return; }
    const r = new SR();
    r.continuous = false; r.interimResults = true; r.lang = "";
    r.onresult = (ev) => {
      const t = Array.from(ev.results).map((x) => x[0].transcript).join("");
      setText((prev) => (prev ? prev + " " : "") + t);
    };
    r.onend = () => setRecording(false);
    r.onerror = () => { setRecording(false); toast.error("Voice recognition error"); };
    recogRef.current = r; setRecording(true); r.start();
  };

  // Camera
  const openCamera = async () => {
    setMenuOpen(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream; setCameraOpen(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch (e) {
      toast.error("Camera access denied or unavailable.");
    }
  };
  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null; setCameraOpen(false);
  };
  const capture = () => {
    const v = videoRef.current; if (!v) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    const dataUrl = c.toDataURL("image/jpeg", 0.85);
    setImage({ base64: dataUrl.split(",")[1], name: "camera-capture.jpg" });
    closeCamera();
  };

  const unavailable = (name) => { toast.message(`${name} requires connection setup. Configure it under Apps.`); setMenuOpen(false); };

  const menuItems = [
    { id: "upload-file", label: "Upload files", desc: "PDF, DOCX, TXT, CSV, XLSX, images", icon: Paperclip, action: () => fileRef.current?.click() },
    { id: "camera", label: "Camera", desc: "Capture a photo", icon: Camera, action: openCamera },
    { id: "photos", label: "Photos / Gallery", desc: "Pick an image", icon: ImageIcon, action: () => imgRef.current?.click() },
    { id: "spreadsheet", label: "Data / Spreadsheet", desc: "Analyze CSV / Excel", icon: Database, action: () => dataRef.current?.click() },
    { id: "drive", label: "Google Drive", desc: "Not connected", icon: Cloud, action: () => unavailable("Google Drive"), muted: true },
    { id: "github", label: "GitHub / Apps", desc: "Not connected", icon: Github, action: () => unavailable("GitHub"), muted: true },
  ];

  return (
    <div className="w-full max-w-4xl mx-auto px-3 pb-4">
      {/* attachments preview */}
      {(image || doc) && (
        <div className="flex flex-wrap gap-2 mb-2">
          {image && (
            <div data-testid="attachment-image" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-200">
              <img src={`data:image/jpeg;base64,${image.base64}`} alt="att" className="w-8 h-8 rounded object-cover" />
              <span className="truncate max-w-[120px]">{image.name}</span>
              <button onClick={() => setImage(null)} className="text-slate-400 hover:text-red-400"><X size={13} /></button>
            </div>
          )}
          {doc && (
            <div data-testid="attachment-document" className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-200">
              <FileText size={14} className="text-emerald-400" />
              <span className="truncate max-w-[160px]">{doc.name}</span>
              <span className="text-slate-500">{doc.charCount ? `${doc.charCount} ch` : ""}</span>
              <button onClick={() => setDoc(null)} className="text-slate-400 hover:text-red-400"><X size={13} /></button>
            </div>
          )}
        </div>
      )}

      <div className="relative bg-[#131C2E] border border-white/10 rounded-2xl p-2.5 shadow-2xl focus-within:border-emerald-500/50 focus-within:ring-2 focus-within:ring-emerald-500/20 transition-all">
        <textarea
          ref={taRef}
          data-testid="composer-input-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask anything — type in any language…"
          rows={1}
          className="w-full bg-transparent text-slate-100 placeholder-slate-500 text-[15px] resize-none focus:outline-none px-1.5 pt-1.5 min-h-[44px] max-h-[200px]"
        />

        <div className="flex items-center gap-1.5 mt-1">
          {/* single + menu */}
          <div className="relative">
            <button
              data-testid="composer-plus-attachment-button"
              onClick={() => setMenuOpen((v) => !v)}
              className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white flex items-center justify-center border border-white/10 transition-colors"
            >
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={19} />}
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                <div data-testid="plus-menu" className="absolute bottom-12 left-0 z-40 w-72 rounded-2xl bg-[#111827] border border-white/10 shadow-2xl p-1.5 ssg-fade-up">
                  {menuItems.map((it) => {
                    const Icon = it.icon;
                    return (
                      <button
                        key={it.id}
                        data-testid={`plus-menu-${it.id}-option`}
                        onClick={it.action}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 text-left transition-colors"
                      >
                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center border border-white/10 ${it.muted ? "bg-white/5 text-slate-500" : "bg-emerald-500/10 text-emerald-400"}`}>
                          <Icon size={16} />
                        </span>
                        <span className="flex-1">
                          <span className="block text-sm text-slate-100">{it.label}</span>
                          <span className="block text-[11px] text-slate-500">{it.desc}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* toggles removed — SSG AI auto-routes web / image / reasoning */}

          <div className="flex-1" />

          <button
            data-testid="composer-voice-mic-button"
            onClick={toggleMic}
            className={`w-9 h-9 rounded-xl flex items-center justify-center border transition-all ${recording ? "bg-red-500/20 border-red-500/50 text-red-400 ssg-recording" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"}`}
          >
            {recording ? <Square size={15} /> : <Mic size={17} />}
          </button>
          <button
            data-testid="composer-send-button"
            onClick={send}
            disabled={disabled || uploading}
            className="w-9 h-9 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center shadow-lg shadow-emerald-500/20 transition-all disabled:opacity-40"
          >
            <ArrowUp size={18} />
          </button>
        </div>
      </div>
      <p className="text-center text-[11px] text-slate-600 mt-2">
        SSG AI can make mistakes. Verify important information. Language is auto-detected.
      </p>

      <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
      <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFiles(e.target.files)} />
      <input ref={dataRef} type="file" accept=".csv,.xls,.xlsx" className="hidden" onChange={(e) => handleFiles(e.target.files)} />

      {cameraOpen && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="camera-modal">
          <div className="bg-[#111827] border border-white/10 rounded-2xl p-4 w-full max-w-md">
            <video ref={videoRef} autoPlay playsInline className="w-full rounded-xl bg-black aspect-video object-cover" />
            <div className="flex gap-2 mt-3">
              <button onClick={closeCamera} className="flex-1 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-300 text-sm">Cancel</button>
              <button data-testid="camera-capture-button" onClick={capture} className="flex-1 py-2 rounded-xl bg-emerald-500 text-white text-sm font-semibold">Capture</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
